import is from '@sindresorhus/is';
import { motion } from 'framer-motion';
import { type FunctionComponent, useCallback, useEffect, useRef } from 'react';

import { theme } from '../../theme/cobrainer-theme';
import { TILE_EXIT_VARIANT, TILE_REFLOW_DURATION, TILE_TRANSITION, TILE_WIDTH_CSS_TRANSITION } from './constants';
import { TileResizeHandle } from './TileResizeHandle';
import { useSetTileBox, useSetTileSlot, useTileErrorBoundary, useTileWidthContext } from './TilingEngineContext';
import type { TilingEngineRightDockProps } from './types';
import { useEndReservation } from './use-end-reservation';
import { useTileExitAnimation } from './use-tile-close-animation';

const COLLAPSED_WIDTH = '0px';
const COLLAPSED_MARGIN_LEFT = `calc(-1 * ${theme.spacing.m})`;

const STICKY_Z_INDEX = 10;

const isCollapsed = (width: string): boolean => width === '0px' || width === '0';

export const TilingEngineRightDock: FunctionComponent<TilingEngineRightDockProps> = ({
    children,
    id,
    maxWidth,
    minWidth,
    resizeKey,
    width = COLLAPSED_WIDTH,
}) => {
    const setTileSlot = useSetTileSlot();
    const setTileBox = useSetTileBox();
    const tileWidths = useTileWidthContext();
    const ErrorBoundary = useTileErrorBoundary();
    const slotRef = useRef<HTMLDivElement>(null);
    const outerRef = useRef<HTMLDivElement | null>(null);
    const { frozenWidth, isPresent, onAnimationComplete } = useTileExitAnimation(id, outerRef);

    const widthStyle = tileWidths.get(id);
    const resolvedWidth = widthStyle?.width ?? width;
    const collapsed = isCollapsed(resolvedWidth);

    useEndReservation({ active: isPresent && !collapsed, id, nodeRef: outerRef });

    const setOuterRef = useCallback(
        (node: HTMLDivElement | null) => {
            outerRef.current = node;
            setTileBox(id, node);
        },
        [id, setTileBox],
    );

    useEffect(() => {
        setTileSlot(id, slotRef.current);

        return () => {
            setTileSlot(id, null);
        };
    }, [id, setTileSlot]);

    // Cancel flex gap-m so collapsed dock takes zero space.
    const gapCancellingMarginLeft = collapsed ? COLLAPSED_MARGIN_LEFT : '0px';

    const resolvedMinWidth = collapsed ? '0px' : (widthStyle?.minWidth ?? minWidth);
    const resolvedMaxWidth = collapsed ? '0px' : (widthStyle?.maxWidth ?? maxWidth);

    return (
        <motion.div
            ref={setOuterRef}
            data-tile-id={id}
            exit={TILE_EXIT_VARIANT}
            style={{
                flexShrink: 0,
                height: '100%',
                overflow: isPresent ? 'visible' : 'hidden',
                position: 'sticky',
                right: 0,
                zIndex: STICKY_Z_INDEX,
                marginLeft: isPresent ? gapCancellingMarginLeft : COLLAPSED_MARGIN_LEFT,
                maxWidth: isPresent ? resolvedMaxWidth : 0,
                minWidth: isPresent ? resolvedMinWidth : 0,
                transition: `${TILE_WIDTH_CSS_TRANSITION}, margin-left ${TILE_REFLOW_DURATION}s ease-out`,
                width: isPresent ? resolvedWidth : 0,
            }}
            transition={{ ...TILE_TRANSITION, duration: TILE_REFLOW_DURATION }}
            variants={{ [TILE_EXIT_VARIANT]: { opacity: 1 } }}
            onAnimationComplete={onAnimationComplete}
        >
            {is.nonEmptyString(resizeKey) && isPresent && !collapsed ? (
                <TileResizeHandle boxRef={outerRef} resizeKey={resizeKey} />
            ) : null}
            <div
                ref={slotRef}
                className="h-full flex flex-col"
                style={{ width: is.number(frozenWidth) ? frozenWidth : '100%' }}
            >
                {is.nullOrUndefined(children) ? null : is.nullOrUndefined(ErrorBoundary) ? (
                    children
                ) : (
                    <ErrorBoundary tileId={id}>{children}</ErrorBoundary>
                )}
            </div>
        </motion.div>
    );
};
